import React, { useEffect, useState } from "react";
import { Avatar, Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import type { User } from "@/shared/types";

const goldLine =
  "linear-gradient(90deg, transparent 0%, rgba(201,164,76,0.35) 20%, rgba(237,216,130,0.9) 50%, rgba(201,164,76,0.35) 80%, transparent 100%)";

const SINGLE_CARD_THANKS_KO = "저의 음악을 사랑해주셔서 감사합니다";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.09,
      delayChildren: 0.06,
      duration: 0.35,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  },
};

/** Real-world name for thank-you line: first + last, else first/last/username. */
export function getFanThankYouDisplayName(
  user: User | null | undefined
): string {
  if (!user) return "";
  const fn = (user.first_name || "").trim();
  const ln = (user.last_name || "").trim();
  if (fn && ln) return `${fn} ${ln}`;
  if (fn) return fn;
  if (ln) return ln;
  return (user.username || "").trim();
}

/**
 * A Single / K-Indie card is considered "sealed" when its seal_time is
 * defined and already in the past. The fan thank-you moment should only
 * appear once that sealed boundary has been crossed.
 */
export function isCardSealed(
  sealTime: string | null | undefined | Date
): boolean {
  if (!sealTime) return false;
  const sealDate = sealTime instanceof Date ? sealTime : new Date(sealTime);
  if (Number.isNaN(sealDate.getTime())) return false;
  return sealDate.getTime() <= Date.now();
}

export type SingleCardFanThankYouPanelProps = {
  /** From logged-in user (first/last name or username). Empty → polite "Friend" lead-in. */
  fanDisplayName?: string | null;
  /** Fan profile image — makes the moment feel one-to-one. */
  fanAvatarUrl?: string | null;
  darkMode: boolean;
  /** Prefer a transparent PNG/SVG of the artist sign; falls back to styled name. */
  signatureImageUrl?: string;
  /** When true, panel is flush inside a parent card (no extra horizontal margin). */
  embedded?: boolean;
};

/**
 * Shared “fan moment” footer for Single Card ending slides (landing + book view).
 */
export const SingleCardFanThankYouPanel: React.FC<
  SingleCardFanThankYouPanelProps
> = ({
  fanDisplayName,
  fanAvatarUrl,
  darkMode,
  signatureImageUrl,
  embedded,
}) => {
  const [hideSignatureImage, setHideSignatureImage] = useState(false);
  const trimmed = (fanDisplayName ?? "").trim();
  const leadName = trimmed || "Friend";
  const avatarInitial = leadName.charAt(0).toUpperCase() || "?";
  const showAvatar = Boolean((fanAvatarUrl ?? "").trim());

  useEffect(() => {
    setHideSignatureImage(false);
  }, [signatureImageUrl]);

  return (
    <Box
      sx={{
        position: "relative",
        flexShrink: 0,
        width: "100%",
        overflow: "hidden",
        background: darkMode
          ? "linear-gradient(165deg, rgba(32, 28, 24, 0.99) 0%, rgba(20, 17, 14, 1) 45%, rgba(14, 12, 10, 1) 100%)"
          : "linear-gradient(165deg, #fffefb 0%, #f7f0e5 42%, #efe6d8 100%)",
        boxShadow: darkMode
          ? "inset 0 1px 0 rgba(255,255,255,0.07), 0 -12px 40px rgba(0,0,0,0.4)"
          : "inset 0 1px 0 rgba(255,255,255,0.9), 0 -10px 36px rgba(90,70,40,0.1)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: goldLine,
          opacity: darkMode ? 0.9 : 1,
          zIndex: 2,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          left: "50%",
          top: "18%",
          transform: "translateX(-50%)",
          width: "120%",
          maxWidth: 420,
          height: "70%",
          background: darkMode
            ? "radial-gradient(ellipse at center, rgba(201,164,76,0.12) 0%, transparent 62%)"
            : "radial-gradient(ellipse at center, rgba(237,216,130,0.22) 0%, transparent 58%)",
          pointerEvents: "none",
          zIndex: 0,
        },
      }}
    >
      <Box
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="show"
        sx={{
          width: "100%",
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          px: embedded ? { xs: 2, sm: 2.5 } : { xs: 2.5, sm: 3.5 },
          py: { xs: 2.5, sm: 3 },
        }}
      >
        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <Box
            sx={{
              position: "relative",
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: darkMode
                ? "linear-gradient(135deg, rgba(201,164,76,0.5), rgba(90,70,40,0.3))"
                : "linear-gradient(135deg, rgba(237,216,130,0.85), rgba(201,164,76,0.45))",
              p: "2px",
              boxShadow: darkMode
                ? "0 0 0 1px rgba(201,164,76,0.25), 0 8px 24px rgba(0,0,0,0.35)"
                : "0 0 0 1px rgba(184,148,62,0.35), 0 8px 22px rgba(80,60,30,0.12)",
              animation: "fanThankYouRing 3.2s ease-in-out infinite",
              "@keyframes fanThankYouRing": {
                "0%, 100%": {
                  boxShadow: darkMode
                    ? "0 0 0 1px rgba(201,164,76,0.25), 0 8px 24px rgba(0,0,0,0.35)"
                    : "0 0 0 1px rgba(184,148,62,0.35), 0 8px 22px rgba(80,60,30,0.12)",
                },
                "50%": {
                  boxShadow: darkMode
                    ? "0 0 0 1px rgba(237,216,130,0.35), 0 8px 28px rgba(201,164,76,0.15)"
                    : "0 0 0 1px rgba(237,216,130,0.65), 0 10px 28px rgba(201,164,76,0.2)",
                },
              },
            }}
          >
            <Avatar
              src={showAvatar ? (fanAvatarUrl as string) : undefined}
              alt=""
              sx={{
                width: "100%",
                height: "100%",
                border: darkMode
                  ? "2px solid rgba(20,18,16,0.95)"
                  : "2px solid rgba(255,252,248,0.95)",
                fontFamily: '"Lexend", sans-serif',
                fontWeight: 700,
                fontSize: "1.25rem",
                bgcolor: darkMode ? "rgba(40,36,32,1)" : "rgba(255,250,242,1)",
                color: darkMode
                  ? "rgba(237,216,130,0.95)"
                  : "rgba(120,85,28,0.9)",
              }}
            >
              {!showAvatar ? avatarInitial : null}
            </Avatar>
          </Box>
        </Box>

        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{ textAlign: "center" }}
        >
          <Typography
            sx={{
              fontSize: "0.62rem",
              fontWeight: 700,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              color: darkMode
                ? "rgba(201,164,76,0.78)"
                : "rgba(120, 85, 28, 0.72)",
              fontFamily: '"Lexend", sans-serif',
              mb: 0.25,
            }}
          >
            <Box
              component="span"
              sx={{
                opacity: 0.85,
                mr: 0.6,
                fontSize: "0.7rem",
                letterSpacing: 0,
              }}
              aria-hidden
            >
              ✦
            </Box>
            A note for you
            <Box
              component="span"
              sx={{
                opacity: 0.85,
                ml: 0.6,
                fontSize: "0.7rem",
                letterSpacing: 0,
              }}
              aria-hidden
            >
              ✦
            </Box>
          </Typography>
        </Box>

        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{ textAlign: "center" }}
        >
          <Typography
            component="div"
            sx={{
              textAlign: "center",
              maxWidth: 360,
              lineHeight: 1.55,
              fontFamily: '"Lexend", sans-serif',
            }}
          >
            <Box
              component="span"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.08rem", sm: "1.15rem" },
                color: darkMode
                  ? "rgba(252, 248, 240, 0.98)"
                  : "rgba(45, 36, 22, 0.95)",
                letterSpacing: "-0.02em",
                mr: 0.5,
              }}
            >
              {leadName},
            </Box>
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "0.95rem", sm: "1.02rem" },
                color: darkMode
                  ? "rgba(235, 228, 215, 0.92)"
                  : "rgba(55, 44, 28, 0.9)",
              }}
            >
              {SINGLE_CARD_THANKS_KO}
            </Box>
          </Typography>
          <Typography
            sx={{
              mt: 1.1,
              fontSize: { xs: "0.8rem", sm: "0.82rem" },
              fontWeight: 500,
              fontStyle: "italic",
              lineHeight: 1.5,
              color: darkMode
                ? "rgba(200, 190, 175, 0.72)"
                : "rgba(90, 75, 55, 0.68)",
              fontFamily: '"Lexend", sans-serif',
              maxWidth: 320,
              mx: "auto",
            }}
          >
            This moment with you matters more than you know — thank you for
            being here.
          </Typography>
        </Box>

        <Box component={motion.div} variants={itemVariants}>
          <Box
            sx={{
              width: { xs: 80, sm: 104 },
              height: 1,
              borderRadius: 1,
              background: goldLine,
              opacity: darkMode ? 0.55 : 0.7,
            }}
          />
        </Box>

        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{ width: "100%", textAlign: "center" }}
        >
          <Typography
            sx={{
              fontFamily: '"WindSong", "Segoe Script", cursive',
              fontSize: { xs: "2.4rem", sm: "2.75rem" },
              fontWeight: 500,
              lineHeight: 1,
              color: darkMode
                ? "rgba(252, 245, 230, 0.92)"
                : "rgba(42, 32, 20, 0.88)",
              textShadow: darkMode
                ? "0 2px 20px rgba(0,0,0,0.5)"
                : "0 2px 12px rgba(255,255,255,0.9)",
              transform: "rotate(-1.5deg)",
              letterSpacing: "0.02em",
            }}
          >
            {signatureImageUrl}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SingleCardFanThankYouPanel;
